/**
 * Voyado Elevate search — drawer autocomplete and search page.
 */

const { ElevateCore } = window;

function debounce(fn, delay) {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

class ElevatePredictiveSearch extends HTMLElement {
  #config = null;
  #fetchAbortController = null;
  #searchForm = null;
  #queryInput = null;

  connectedCallback() {
    this.#config = ElevateCore.readConfigFromElement(this);

    if (!this.#config?.clusterId || !this.#config.market || !this.#config.locale) {
      return;
    }

    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment('<slot name="placeholder"></slot>'));

    this.#searchForm = document.querySelector(`[aria-owns="${this.id}"]`);
    this.#queryInput = this.#searchForm?.elements?.q;

    if (!this.#searchForm || !this.#queryInput) {
      return;
    }

    this.#searchForm.addEventListener('submit', this.#onFormSubmitted.bind(this));
    this.#searchForm.addEventListener('reset', this.#onSearchCleared.bind(this));
    this.#queryInput.addEventListener(
      'input',
      debounce(this.#onInputChanged.bind(this), Math.min(parseInt(this.dataset.autocompleteDelay || '80', 10), 80))
    );
    this.#loadDiscover();
  }

  async #loadDiscover() {
    try {
      const data = await ElevateCore.fetchAutocomplete(this.#config, {
        q: '',
        priority: 'high'
      });
      const result = ElevateCore.extractAutocompleteProducts(data);
      const recentRoot = this.querySelector('[data-elevate-recent]');
      const trendingRoot = this.querySelector('[data-elevate-trending]');
      const menu = this.querySelector('[data-elevate-popular-menu]');
      const recentLinks = ElevateCore.buildRecentSearchLinks(result.recentSearches, this.#searchUrl());
      const trendingLinks = ElevateCore.buildPhraseSuggestionLinks(result.phraseSuggestions, this.#searchUrl());

      if (recentRoot && recentLinks) {
        recentRoot.querySelector('[data-elevate-recent-links]').innerHTML =
          `<ul class="unstyled-list v-stack justify-items-start gap-3.5">${recentLinks}</ul>`;
        recentRoot.hidden = false;
      }

      if (trendingRoot && trendingLinks) {
        trendingRoot.querySelector('[data-elevate-trending-links]').innerHTML =
          `<ul class="unstyled-list v-stack justify-items-start gap-3.5">${trendingLinks}</ul>`;
        trendingRoot.hidden = false;

        if (menu) {
          menu.hidden = true;
        }
      }

      ElevateCore.bindClickTracking(this, this.#config);
    } catch (_error) {
      // Keep the menu fallback.
    }
  }

  #searchUrl() {
    return this.#searchForm.action || `${Shopify.routes.root}search`;
  }

  #onFormSubmitted(event) {
    if (this.#queryInput.value === '') {
      event.preventDefault();
    }
  }

  #onSearchCleared() {
    this.#fetchAbortController?.abort();
    this.#queryInput.focus();
    this.shadowRoot.innerHTML = '<slot name="placeholder"></slot>';
  }

  async #onInputChanged() {
    if (this.#queryInput.value === '') {
      return this.#onSearchCleared();
    }

    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const data = await ElevateCore.fetchAutocomplete(this.#config, {
        q: this.#queryInput.value,
        signal: this.#fetchAbortController.signal,
        priority: 'high'
      });

      const result = ElevateCore.extractAutocompleteProducts(data);
      const productMarkup = ElevateCore.buildAutocompleteProducts(
        result.productSuggestions,
        this.#config.limit || 8
      );

      this.shadowRoot.innerHTML = '<slot name="results"></slot>';
      const resultsSlot = this.querySelector('[slot="results"]');
      const wrapper = document.createElement('div');

      const phraseSuggestions = ElevateCore.buildPhraseSuggestionLinks(result.phraseSuggestions, this.#searchUrl());
      const contentMarkup = ElevateCore.buildContentMarkup(
        result.contentLists,
        this.dataset.contentTitle || ''
      );

      if (phraseSuggestions) {
        const suggestionsList = document.createElement('ul');
        suggestionsList.className = 'predictive-search__suggestions unstyled-list scroll-area bleed';
        suggestionsList.innerHTML = phraseSuggestions;
        wrapper.appendChild(suggestionsList);
      }

      if (contentMarkup) {
        const contentWrap = document.createElement('div');
        contentWrap.className = 'predictive-search__content v-stack gap-4';
        contentWrap.innerHTML = contentMarkup;
        wrapper.appendChild(contentWrap);
      }

      if (productMarkup) {
        const productsWrapper = document.createElement('div');
        productsWrapper.innerHTML = productMarkup;
        wrapper.append(...productsWrapper.childNodes);
      } else if (!phraseSuggestions && !contentMarkup) {
        const recentSuggestions = ElevateCore.buildRecentSearchLinks(result.recentSearches, this.#searchUrl());

        if (recentSuggestions) {
          const recentList = document.createElement('ul');
          recentList.className = 'predictive-search__suggestions unstyled-list scroll-area bleed';
          recentList.innerHTML = recentSuggestions;
          wrapper.appendChild(recentList);
        }

        const noResults = document.createElement('div');
        noResults.className = 'predictive-search__no-results prose prose-justify-center';
        noResults.innerHTML = `
          <p class="subheading">${ElevateCore.escapeHtml(this.dataset.noResultsTitle || 'Search')}</p>
          <p class="h5">${ElevateCore.escapeHtml(this.dataset.noResultsText || 'No results could be found.')}</p>
        `;
        wrapper.appendChild(noResults);
      } else {
        const noProducts = document.createElement('div');
        noProducts.className = 'predictive-search__no-results prose prose-justify-center';
        noProducts.innerHTML = `<p class="h5">${ElevateCore.escapeHtml(this.dataset.noResultsText || 'No results could be found.')}</p>`;
        wrapper.appendChild(noProducts);
      }

      resultsSlot.replaceChildren(...wrapper.childNodes);
      ElevateCore.bindClickTracking(resultsSlot, this.#config);
    } catch (error) {
      if (error.name !== 'AbortError') {
        throw error;
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
}

class ElevateSearchPage extends HTMLElement {
  #config = null;

  connectedCallback() {
    this.#config = ElevateCore.readConfigFromElement(this);

    if (!this.#config?.clusterId || !this.#config.market || !this.#config.locale) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q')?.trim();

    if (!query) {
      return;
    }

    if (!this.dataset.controlsBound) {
      this.dataset.controlsBound = 'true';
      this.addEventListener('change', (event) => {
        const sort = event.target.closest('[data-elevate-sort]');

        if (sort?.form) {
          sort.form.requestSubmit();
        }
      });
      this.addEventListener('submit', (event) => {
        const form = event.target;

        if (!(form instanceof HTMLFormElement) || !form.classList.contains('elevate-search-facets__range')) {
          return;
        }

        form.querySelectorAll('input[type="number"]').forEach((input) => {
          if (input.value === '') {
            input.disabled = true;
          }
        });
      });
    }

    this.loadResults(query);
  }

  async loadResults(inputQuery) {
    const urlParams = new URLSearchParams(window.location.search);
    const page = Math.max(parseInt(urlParams.get('page') || '1', 10), 1);
    const sort = urlParams.get('sort') || '';
    const origin = urlParams.get('origin') || 'ORGANIC';
    const filters = this.#readFilters(urlParams);
    const skip = (page - 1) * this.#config.productsPerPage;
    const filterKey = Object.keys(filters).sort().map((key) => `${key}=${filters[key]}`).join('&');
    const loadKey = [inputQuery, page, sort, origin, skip, filterKey].join('|');

    if (this.dataset.loadedKey === loadKey) {
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const data = await ElevateCore.fetchSearchPage(this.#config, {
        q: inputQuery,
        limit: this.#config.productsPerPage,
        skip,
        sort: sort || undefined,
        origin,
        filters
      });

      const result = ElevateCore.extractPrimaryListProducts(data);
      const secondary = ElevateCore.extractSecondaryListProducts(data);
      const effectiveQuery = result.effectiveQuery || inputQuery;
      const resultsContainer = this.querySelector('[data-elevate-search-results]');
      const countContainer = this.querySelector('[data-elevate-search-count]');
      const metaContainer = this.querySelector('[data-elevate-search-meta]');
      const paginationContainer = this.querySelector('[data-elevate-search-pagination]');
      const secondaryContainer = this.querySelector('[data-elevate-search-secondary]');
      const secondaryTitle = this.querySelector('[data-elevate-secondary-title]');
      const secondaryResults = this.querySelector('[data-elevate-search-secondary-results]');

      if (countContainer) {
        const template = result.totalHits === 1 ? countContainer.dataset.countOne : countContainer.dataset.countOther;
        countContainer.textContent = (template || '{{ count }} results').replace('__COUNT__', String(result.totalHits));
      }

      if (metaContainer) {
        metaContainer.innerHTML = ElevateCore.buildSearchMetaMarkup({
          autoCorrect: result.autoCorrect,
          didYouMean: result.didYouMean,
          q: inputQuery,
          searchUrl: `${Shopify.routes.root}search`
        });
      }

      const contentContainer = this.querySelector('[data-elevate-search-content]');
      const contentMarkup = ElevateCore.buildContentMarkup(
        result.contentLists,
        this.dataset.contentTitle || ''
      );

      if (contentContainer) {
        if (contentMarkup) {
          contentContainer.innerHTML = contentMarkup;
          contentContainer.hidden = false;
        } else {
          contentContainer.innerHTML = '';
          contentContainer.hidden = true;
        }
      }

      this.#renderControls(result.facets, result.sort, filters);

      if (!resultsContainer) {
        return;
      }

      if (!result.productIds.length) {
        const hasFilters = Object.keys(filters).length > 0;
        const emptyText = hasFilters
          ? (this.dataset.filteredEmptyText || this.dataset.noResultsText || 'No products match those filters.')
          : (this.dataset.noResultsText || 'No results could be found.');
        const clearLink = hasFilters
          ? `<a class="subheading link" href="${ElevateCore.escapeAttr(this.#clearFacetsHref())}">${ElevateCore.escapeHtml(this.dataset.clearLabel || 'Clear all')}</a>`
          : '';

        resultsContainer.innerHTML = `
          <div class="empty-state empty-state--sm">
            <div class="prose prose-justify-center prose--compact">
              <p>${ElevateCore.escapeHtml(emptyText)}</p>
              ${clearLink}
            </div>
          </div>
        `;

        if (paginationContainer) {
          paginationContainer.innerHTML = '';
        }

        if (secondaryContainer) {
          secondaryContainer.hidden = true;
          secondaryResults.innerHTML = '';
        }
      } else {
        const sectionNode = await ElevateCore.renderProductsSection(
          this.#config,
          result.productIds,
          this.#config.resultsSectionId
        );

        if (!sectionNode) {
          return;
        }

        resultsContainer.innerHTML = sectionNode.innerHTML;
        ElevateCore.attachTickets(resultsContainer, result.tickets, result.productIds);
        ElevateCore.bindClickTracking(resultsContainer, this.#config);

        if (paginationContainer) {
          paginationContainer.innerHTML = this.#buildPagination({
            effectiveQuery,
            page,
            totalHits: result.totalHits,
            sort,
            origin
          });
        }
      }

      if (secondaryContainer && secondaryResults) {
        if (secondary.productIds.length) {
          const secondarySectionNode = await ElevateCore.renderProductsSection(
            this.#config,
            secondary.productIds,
            this.#config.secondaryResultsSectionId
          );

          if (secondarySectionNode) {
            secondaryTitle.textContent = secondary.title || this.dataset.secondaryTitle || 'Related results';
            secondaryResults.innerHTML = secondarySectionNode.innerHTML;
            ElevateCore.attachTickets(secondaryResults, secondary.tickets, secondary.productIds);
            ElevateCore.bindClickTracking(secondaryResults, this.#config);
            secondaryContainer.hidden = false;
          } else {
            secondaryContainer.hidden = true;
            secondaryResults.innerHTML = '';
          }
        } else {
          secondaryContainer.hidden = true;
          secondaryResults.innerHTML = '';
        }
      }

      this.closest('.shopify-section')?.querySelector('.empty-state')?.remove();
      this.hidden = false;
      this.dataset.loadedKey = loadKey;

      if (effectiveQuery !== inputQuery && page === 1 && !urlParams.get('origin')) {
        const url = new URL(window.location.href);
        url.searchParams.set('q', effectiveQuery);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

        const searchInput = this.querySelector('input[name="q"]');

        if (searchInput) {
          searchInput.value = effectiveQuery;
        }
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  // Collection page filters (Collection, Fragrance) are client-side tag/handle
  // filters. Elevate has no fragrance facet. custom.tags and custom.Collection
  // are feed metadata (itp-*, ItemGroup, Obsolete), not those filters.
  // Size, price, and department are the facets that match the collection page.
  #isOmittedFacet(facet) {
    const id = String(facet?.id || '').toLowerCase();
    return facet?.type === 'COLOR' || id === 'brand' || id === 'color' || id === 'custom.tags' || id === 'custom.collection';
  }

  #isOmittedFilterKey(key) {
    return key === 'f.brand' || key.startsWith('f.brand.') || key === 'f.color' || key.startsWith('f.color.') || key === 'f.custom.tags' || key.startsWith('f.custom.tags.') || key.toLowerCase() === 'f.custom.collection' || key.toLowerCase().startsWith('f.custom.collection.');
  }

  #isInternalFacetValue(value) {
    const label = String(value?.label || value?.id || '').trim();
    return !label || label.startsWith('__');
  }

  #readFilters(urlParams) {
    const filters = {};

    urlParams.forEach((value, key) => {
      if (key.startsWith('f.') && value && !this.#isOmittedFilterKey(key)) {
        filters[key] = value;
      }
    });

    return filters;
  }

  #hiddenFields(skipKeys) {
    const params = new URLSearchParams(window.location.search);
    const fields = [];

    params.forEach((value, key) => {
      if (key === 'page' || skipKeys.has(key) || this.#isOmittedFilterKey(key)) {
        return;
      }

      fields.push(`<input type="hidden" name="${ElevateCore.escapeAttr(key)}" value="${ElevateCore.escapeAttr(value)}">`);
    });

    return fields.join('');
  }

  #mutateSearchUrl(mutate) {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    [...url.searchParams.keys()].filter((key) => this.#isOmittedFilterKey(key)).forEach((key) => url.searchParams.delete(key));
    mutate(url.searchParams);
    url.hash = 'collection';
    return `${url.pathname}${url.search}${url.hash}`;
  }

  #toggleFacetHref(param, valueId, selected) {
    return this.#mutateSearchUrl((params) => {
      const current = (params.get(param) || '').split('|').filter(Boolean);
      const next = selected ? current.filter((id) => id !== valueId) : [...new Set([...current, valueId])];

      if (next.length) {
        params.set(param, next.join('|'));
      } else {
        params.delete(param);
      }
    });
  }

  #clearFacetsHref() {
    return this.#mutateSearchUrl((params) => {
      [...params.keys()].filter((key) => key.startsWith('f.')).forEach((key) => params.delete(key));
    });
  }

  #facetValues(facet) {
    if (Array.isArray(facet.values)) {
      return facet.values;
    }

    if (facet.type !== 'SIZE' || !Array.isArray(facet.sizeTypes)) {
      return [];
    }

    const values = [];

    facet.sizeTypes.forEach((sizeType) => {
      (sizeType.formats || []).forEach((format) => {
        (format.values || []).forEach((value) => values.push(value));
      });
    });

    return values;
  }

  #renderControls(facets, sort, filters) {
    const sortContainer = this.querySelector('[data-elevate-search-sort]');
    const facetsContainer = this.querySelector('[data-elevate-search-facets]');

    if (sortContainer) {
      sortContainer.innerHTML = this.#buildSortMarkup(sort, filters);
    }

    if (!facetsContainer) {
      return;
    }

    const markup = this.#buildFacetsMarkup(facets, filters);
    facetsContainer.innerHTML = markup;
    facetsContainer.hidden = markup === '';
  }

  #buildSortMarkup(sort) {
    const options = Array.isArray(sort?.options) ? sort.options : [];

    if (!options.length) {
      return '';
    }

    const selected = sort.selected || '';
    const action = `${Shopify.routes.root}search`;

    return `
      <form class="elevate-search-sort" method="get" action="${ElevateCore.escapeAttr(action)}">
        ${this.#hiddenFields(new Set(['sort']))}
        <select class="select" name="sort" aria-label="${ElevateCore.escapeAttr(this.dataset.sortLabel || 'Sort by')}" data-elevate-sort>
          ${options.map((option) => `
            <option value="${ElevateCore.escapeAttr(option.id)}"${option.id === selected ? ' selected' : ''}>${ElevateCore.escapeHtml(option.label || option.id)}</option>
          `).join('')}
        </select>
      </form>
    `;
  }

  #visibleFacets(facets) {
    const rank = (facet) => {
      const id = String(facet?.id || '').toLowerCase();

      if (id === 'size' || facet?.type === 'SIZE') {
        return 0;
      }

      if (id === 'price' || facet?.type === 'RANGE') {
        return 2;
      }

      if (id === 'department' || id === 'category') {
        return 3;
      }

      return 4;
    };

    return (Array.isArray(facets) ? facets : [])
      .filter((facet) => !this.#isOmittedFacet(facet))
      .sort((a, b) => rank(a) - rank(b));
  }

  #buildFacetsMarkup(facets, filters) {
    const groups = this.#visibleFacets(facets)
      .map((facet) => this.#buildFacetGroup(facet, filters))
      .filter(Boolean);

    if (!groups.length) {
      return '';
    }

    const chips = this.#buildActiveFacets(facets, filters);

    return `
      <div class="facets elevate-search-facets">
        ${chips}
        <div class="elevate-search-facets__groups">${groups.join('')}</div>
      </div>
    `;
  }

  #buildFacetGroup(facet, filters) {
    if (!facet?.id || facet.type === 'RANGE') {
      return facet?.type === 'RANGE' ? this.#buildRangeFacet(facet, filters) : '';
    }

    if (facet.type === 'CHECKBOX') {
      return this.#buildCheckboxFacet(facet, filters);
    }

    const values = this.#facetValues(facet).filter(
      (value) => value && (value.count > 0 || value.selected) && !this.#isInternalFacetValue(value)
    );

    if (!values.length) {
      return '';
    }

    const param = `f.${facet.id}`;
    const options = values.map((value) => {
      const selected = Boolean(value.selected);
      const href = this.#toggleFacetHref(param, value.id, selected);
      const swatch = facet.type === 'COLOR' && value.color
        ? `<span class="elevate-search-facets__swatch" style="--swatch: ${ElevateCore.escapeAttr(value.color)}"></span>`
        : '';

      return `
        <a class="elevate-search-facets__option${selected ? ' is-selected' : ''}" href="${ElevateCore.escapeAttr(href)}" aria-pressed="${selected ? 'true' : 'false'}">
          ${swatch}
          <span>${ElevateCore.escapeHtml(value.label || value.id)}</span>
          ${Number.isFinite(value.count) ? `<span class="elevate-search-facets__count">${value.count}</span>` : ''}
        </a>
      `;
    }).join('');

    return this.#facetAccordion(facet.label, facet.selectedCount, options);
  }

  #buildCheckboxFacet(facet, filters) {
    const param = `f.${facet.id}`;
    const selected = filters[param] === 'true' || facet.selected === true;
    const href = this.#mutateSearchUrl((params) => {
      if (selected) {
        params.delete(param);
      } else {
        params.set(param, 'true');
      }
    });

    const option = `
      <a class="elevate-search-facets__option${selected ? ' is-selected' : ''}" href="${ElevateCore.escapeAttr(href)}" aria-pressed="${selected ? 'true' : 'false'}">
        <span>${ElevateCore.escapeHtml(facet.label || facet.id)}</span>
      </a>
    `;

    return this.#facetAccordion(facet.label, selected ? 1 : 0, option);
  }

  #buildRangeFacet(facet, filters) {
    const minKey = `f.${facet.id}.min`;
    const maxKey = `f.${facet.id}.max`;
    const minValue = filters[minKey] || '';
    const maxValue = filters[maxKey] || '';
    const selectedCount = minValue || maxValue ? 1 : 0;
    const action = `${Shopify.routes.root}search`;
    const content = `
      <form class="elevate-search-facets__range" method="get" action="${ElevateCore.escapeAttr(action)}">
        ${this.#hiddenFields(new Set([minKey, maxKey]))}
        <label>
          <span class="sr-only">${ElevateCore.escapeHtml(this.dataset.priceFromLabel || 'From')}</span>
          <input class="input" type="number" inputmode="decimal" name="${ElevateCore.escapeAttr(minKey)}" min="${ElevateCore.escapeAttr(facet.min ?? '')}" max="${ElevateCore.escapeAttr(facet.max ?? '')}" placeholder="${ElevateCore.escapeAttr(facet.min ?? '')}" value="${ElevateCore.escapeAttr(minValue)}" aria-label="${ElevateCore.escapeAttr(this.dataset.priceFromLabel || 'From')}">
        </label>
        <label>
          <span class="sr-only">${ElevateCore.escapeHtml(this.dataset.priceToLabel || 'To')}</span>
          <input class="input" type="number" inputmode="decimal" name="${ElevateCore.escapeAttr(maxKey)}" min="${ElevateCore.escapeAttr(facet.min ?? '')}" max="${ElevateCore.escapeAttr(facet.max ?? '')}" placeholder="${ElevateCore.escapeAttr(facet.max ?? '')}" value="${ElevateCore.escapeAttr(maxValue)}" aria-label="${ElevateCore.escapeAttr(this.dataset.priceToLabel || 'To')}">
        </label>
        <button class="button button--sm" type="submit">${ElevateCore.escapeHtml(this.dataset.applyLabel || 'Apply')}</button>
      </form>
    `;

    return this.#facetAccordion(facet.label, selectedCount, content, selectedCount > 0);
  }

  #facetAccordion(label, selectedCount, content, open = false) {
    const count = Number(selectedCount) > 0 ? ` (${selectedCount})` : '';
    const isOpen = open || Number(selectedCount) > 0;

    return `
      <accordion-disclosure class="accordion${isOpen ? ' is-open' : ''}">
        <details class="accordion__disclosure"${isOpen ? ' open' : ''}>
          <summary class="accordion__summary subheading">
            <span>${ElevateCore.escapeHtml(label || '')}${count}</span>
            <svg aria-hidden="true" focusable="false" width="11" class="icon-accordion-small-arrow" viewBox="0 0 11 7" fill="none"><path stroke="currentColor" stroke-width="1.5" d="M10 1 5.5 5.5 1 1"/></svg>
          </summary>
          <div class="accordion__content">
            <div class="elevate-search-facets__options">${content}</div>
          </div>
        </details>
      </accordion-disclosure>
    `;
  }

  #buildActiveFacets(facets, filters) {
    const chips = [];
    const deleteIcon = '<svg aria-hidden="true" focusable="false" width="9" viewBox="0 0 9 9"><path stroke="currentColor" d="m1 1 7 7m0-7L1 8"/></svg>';

    (Array.isArray(facets) ? facets : []).forEach((facet) => {
      if (this.#isOmittedFacet(facet)) {
        return;
      }

      if (facet.type === 'RANGE') {
        const min = filters[`f.${facet.id}.min`];
        const max = filters[`f.${facet.id}.max`];

        if (!min && !max) {
          return;
        }

        const href = this.#mutateSearchUrl((params) => {
          params.delete(`f.${facet.id}.min`);
          params.delete(`f.${facet.id}.max`);
        });
        const name = [min, max].filter(Boolean).join(' – ');
        chips.push(this.#facetChip(`${facet.label}: ${name}`, href, name, deleteIcon));
        return;
      }

      if (facet.type === 'CHECKBOX' && (filters[`f.${facet.id}`] === 'true' || facet.selected === true)) {
        const href = this.#mutateSearchUrl((params) => params.delete(`f.${facet.id}`));
        chips.push(this.#facetChip(facet.label, href, facet.label, deleteIcon));
        return;
      }

      this.#facetValues(facet).filter((value) => value?.selected).forEach((value) => {
        const href = this.#toggleFacetHref(`f.${facet.id}`, value.id, true);
        chips.push(this.#facetChip(value.label || value.id, href, value.label || value.id, deleteIcon));
      });
    });

    if (!chips.length) {
      return '';
    }

    const clear = chips.length > 1
      ? `<a href="${ElevateCore.escapeAttr(this.#clearFacetsHref())}" class="facets-clear-all subheading link">${ElevateCore.escapeHtml(this.dataset.clearLabel || 'Clear all')}</a>`
      : '';

    return `<div class="active-facets">${chips.join('')}${clear}</div>`;
  }

  #facetChip(text, href, name, deleteIcon) {
    const removeLabel = (this.dataset.removeFilterLabel || 'Remove filter __NAME__').replace('__NAME__', name || text);

    return `
      <div class="removable-facet subheading">
        ${ElevateCore.escapeHtml(text)}
        <a href="${ElevateCore.escapeAttr(href)}" class="tap-area tap-area--sm" aria-label="${ElevateCore.escapeAttr(removeLabel)}">${deleteIcon}</a>
      </div>
    `;
  }

  #buildPagination({ effectiveQuery, page, totalHits, sort, origin }) {
    const totalPages = Math.max(Math.ceil(totalHits / this.#config.productsPerPage), 1);

    if (totalPages <= 1) {
      return '';
    }

    const buildUrl = (targetPage) => {
      const url = new URL(window.location.href);
      [...url.searchParams.keys()].filter((key) => this.#isOmittedFilterKey(key)).forEach((key) => url.searchParams.delete(key));
      url.searchParams.set('q', effectiveQuery);
      url.searchParams.set('page', String(targetPage));

      if (sort) {
        url.searchParams.set('sort', sort);
      } else {
        url.searchParams.delete('sort');
      }

      if (origin && origin !== 'ORGANIC') {
        url.searchParams.set('origin', origin);
      } else {
        url.searchParams.delete('origin');
      }

      url.hash = 'collection';
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const previousDisabled = page <= 1;
    const nextDisabled = page >= totalPages;
    const prevControl = previousDisabled
      ? '<span class="circle-button circle-button--lg" aria-disabled="true">‹</span>'
      : `<a class="circle-button circle-button--lg" rel="prev" href="${buildUrl(page - 1)}" aria-label="Previous page">‹</a>`;
    const nextControl = nextDisabled
      ? '<span class="circle-button circle-button--lg" aria-disabled="true">›</span>'
      : `<a class="circle-button circle-button--lg" rel="next" href="${buildUrl(page + 1)}" aria-label="Next page">›</a>`;

    return `
      <nav class="pagination" role="navigation" aria-label="Pagination">
        ${prevControl}
        <span class="subheading" aria-current="page">${page} / ${totalPages}</span>
        ${nextControl}
      </nav>
    `;
  }
}

if (!window.customElements.get('elevate-predictive-search')) {
  window.customElements.define('elevate-predictive-search', ElevatePredictiveSearch);
}

if (!window.customElements.get('elevate-search-page')) {
  window.customElements.define('elevate-search-page', ElevateSearchPage);
}
